import type { Component } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';
import { appModules } from '@modules/registry.js';
import type { Permission } from '@shared/infrastructure/auth/permission.js';
import { authGuard } from '../auth/guard.js';
import { composeRoutes } from './composition.js';
import { scrollFor } from './scroll-behavior.js';

/**
 * Application router.
 *
 * Every route is declared at boot: none is registered on the fly. That leaves
 * entry to a screen up to the guard —one single rule, in one place— and not up
 * to the order in which things happened to load.
 */
export const router = createRouter({
  history: createWebHistory(),
  routes: composeRoutes(appModules),
  // In `scroll-behavior.ts`, where it can be tested: two of its three cases need a real browser
  // and a history to see, and it was wrong in one of them for months.
  scrollBehavior: (to, from, savedPosition) => scrollFor(to, from, savedPosition),
});

router.beforeEach(authGuard);

declare module 'vue-router' {
  interface RouteMeta {
    /** Explicit opt-in: without this, the route asks for a session. */
    public?: boolean;
    /** Marks the route that hosts the shell. Only one. */
    layoutHost?: boolean;
    /**
     * Permission the route asks for. Declared here and nowhere else: the guard
     * reads it to let people in and the sidebar reads it to decide what to show,
     * so the two can never disagree.
     */
    permission?: Permission;
    /**
     * A detail. The rule of the back-office: a form is a modal; a detail is a page
     * of its own, and it slides in from the right over the page it was opened
     * from, and back out over the page it is left for. The shell does both; see
     * its `use-page-slide.ts`.
     */
    slidesIn?: boolean;
    /**
     * The navigation of the section the page belongs to. The shell draws it above
     * the page and outside the page's transition, so it stays put while details
     * slide under it; a page does not draw it itself.
     */
    sectionNav?: Component;
  }
}
